import { BatchMetadataItem, ProblematicMetadataRow } from '../../../core/models/api.models';

export interface ProblematicMetadataGroup {
  metadataId: number;
  rows: ProblematicMetadataRow[];
  investorCount: number;
  avancementCount: number;
  consultantNames: string[];
  displayNames: string[];
}

export function groupProblematicRowsByMetadata(
  rows: ProblematicMetadataRow[]
): Array<[number, ProblematicMetadataRow[]]> {
  const groupedRows = new Map<number, ProblematicMetadataRow[]>();

  rows.forEach(row => {
    const metadataRows = groupedRows.get(row.metadataId) ?? [];
    metadataRows.push(row);
    groupedRows.set(row.metadataId, metadataRows);
  });

  return Array.from(groupedRows.entries());
}

export function buildProblematicMetadataGroups(rows: ProblematicMetadataRow[]): ProblematicMetadataGroup[] {
  return groupProblematicRowsByMetadata(rows)
    .map(([metadataId, metadataRows]) => ({
      metadataId,
      rows: [...metadataRows].sort((left, right) =>
        left.investorId - right.investorId || left.avancementId - right.avancementId),
      investorCount: new Set(metadataRows.map(row => row.investorId)).size,
      avancementCount: metadataRows.length > 0 ? metadataRows[0].avancementCount : 0,
      consultantNames: getDistinctNonEmptyStrings(metadataRows.map(row => row.consultantName)),
      displayNames: getDistinctNonEmptyStrings(metadataRows.map(row => row.displayName))
    }))
    .sort((left, right) => left.metadataId - right.metadataId);
}

export function filterProblematicMetadataGroups(
  groups: ProblematicMetadataGroup[],
  filterValue: string
): ProblematicMetadataGroup[] {
  const filter = normalizeProblematicMetadataFilter(filterValue);

  if (!filter) {
    return groups;
  }

  return groups.filter(group => group.rows.some(row => getProblematicMetadataRowSearchText(row).includes(filter)));
}

export function buildBatchItems(rows: ProblematicMetadataRow[]): BatchMetadataItem[] {
  return buildProblematicMetadataGroups(rows).map(group => ({
    metadataId: group.metadataId,
    investorCount: group.investorCount,
    avancementCount: group.avancementCount,
    consultantNames: group.consultantNames,
    displayNames: group.displayNames
  }));
}

export function getDistinctNonEmptyStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => !!value)));
}

function getProblematicMetadataRowSearchText(row: ProblematicMetadataRow): string {
  return Object.values(row)
    .filter((value): value is string | number => value !== null && value !== undefined)
    .map(value => value.toString().toLocaleLowerCase())
    .join(' ');
}

function normalizeProblematicMetadataFilter(value: string): string {
  return value.trim().toLocaleLowerCase();
}
